import LoadingOnFront from './components/LoadingOnFront';
import StepContainer from './components/StepContainer';
import { QuoteBuilderStep, initialQuoteContext } from './utils/formState';
import { generateNewQuote } from './utils/data';
import { setCurrentPageLeaver } from './utils/quoteTracking';
import {
  trackingPageNames,
  useCurrentUrlStep,
  useProceedHandler,
  useValidationStepSchema,
} from './QuoteBuilder.methods';

import { TrackingEvent } from '../../utils/tracking/types';
import { authenticated$ } from '../../store/session/selectors';
import { catalogueId$ } from '../../store/cart/selectors';
import { createQuotePerform } from '../../store/quotes/actions';
import { loadingCartCatalogue$ } from '../../store/loading/selectors';
import { requestCodePerform, signupOrSigninPerform } from '../../store/session/actions';
import { signUpPayload } from '../SignUpForm/utils/form';
import { trackSignUpQuote } from '../../utils/authUtils';
import { PrivateRoutePath } from '../../utils/routes';
import { AccountField } from '../../utils/forms';
import tracking from '../../utils/tracking';
import FullScreenLayout from '../FullScreenLayout';
import FormikWrapper from '../FormikWrapper';
import PageHead from '../PageHead';

import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { QuoteBuilderValues } from './utils/formState';
import type { ReactNode } from 'react';
import type { FormikHelpers } from 'formik';

const id = 'quote-builder';

declare global {
  interface Window {
    ReactNativeWebView: {
      postMessage(msg: string): void;
    };
  }
}

interface QuoteBuilderProps {
  children: ReactNode;
}

function QuoteBuilder({ children }: QuoteBuilderProps) {
  const dispatch = useDispatch();
  const { push } = useRouter();

  const isCatalogueLoading = useSelector(loadingCartCatalogue$);
  const catalogueId = useSelector(catalogueId$);
  const authenticated = useSelector(authenticated$);

  const handleNextStep = useProceedHandler();
  const { urlStep } = useCurrentUrlStep();
  const schema = useValidationStepSchema(urlStep);

  const isInitializing = !catalogueId && isCatalogueLoading;

  const onSubmit = (values: QuoteBuilderValues, actions: FormikHelpers<QuoteBuilderValues>) => {
    // Second last step, before sms verification input.
    if (urlStep === QuoteBuilderStep.REGISTER) {
      const callback = () => {
        tracking.event(TrackingEvent.NEW_QUOTE_CONTACT_DETAILS_ENTERED);
        handleNextStep(values, actions);
      };
      dispatch(requestCodePerform(values[AccountField.MOBILE], { callback }));
    }
    // Final step; submit the payload.
    else if (urlStep === QuoteBuilderStep.MOBILE) {
      const registerPayload = signUpPayload(values);

      const quotePayload = generateNewQuote(values, catalogueId);

      const profileCallback = () => {
        if (quotePayload) {
          if (authenticated) {
            dispatch(createQuotePerform(quotePayload));
          } else {
            window.localStorage.setItem('quotePayload', JSON.stringify(quotePayload));
          }
        }
        setCurrentPageLeaver(trackingPageNames[urlStep], 'orderListing');
        void push(PrivateRoutePath.ORDERS);
        actions.resetForm();
        window.sessionStorage.removeItem(id);
      };

      const trackingCallback = (isNewCustomer: boolean) => {
        if (isNewCustomer) {
          trackSignUpQuote(values[AccountField.IS_TRADE_CUSTOMER]);
        }
      };

      dispatch(signupOrSigninPerform(registerPayload, { profileCallback, trackingCallback }));
    } else if (urlStep === QuoteBuilderStep.MIX && authenticated) {
      const quotePayload = generateNewQuote(values, catalogueId);

      const callback = (newQuoteId: string) => {
        window.ReactNativeWebView?.postMessage(`embeddedQuoteFinished-${newQuoteId}`);
        handleNextStep(values, actions, newQuoteId);
      };

      if (quotePayload) {
        dispatch(createQuotePerform(quotePayload, { callback }));
      } else {
        handleNextStep(values, actions);
      }
    } else {
      handleNextStep(values, actions);
    }
  };

  useEffect(() => {
    tracking.event(TrackingEvent.NEW_QUOTE_STARTED);
  }, []);

  return (
    <FullScreenLayout>
      <PageHead title="NEW_QUOTE" />
      <FormikWrapper
        // formikSessionState={formikSessionItem}
        formikSessionStateKey={id}
        // Set the status to persist each completed step (represented by number)
        initialStatus={0}
        initialValues={initialQuoteContext}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validationSchema={schema}
      >
        <LoadingOnFront isLoading={isInitializing}>
          <StepContainer>{children}</StepContainer>
        </LoadingOnFront>
      </FormikWrapper>
    </FullScreenLayout>
  );
}

export default QuoteBuilder;
